import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";
import statusColors from "../utils/statusColors";
import Select from "react-select";
import Pagination from "../components/Pagination";
import {
  createSupplier,
  getSupplier,
  listSuppliers,
  updateSupplier,
  listBankAccounts,
  postSupplierConversion,
} from "../erpClient";
import { createErpSocket } from "../utils/socketIo";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  enforceMinusIfNegative,
  getMaxAllowedValidationMessage,
  getMaxAllowedAmountValidationMessage,
} from "../utils/metalAmountValidation";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  getMobileValidationError,
} from "../utils/mobileInput";
import {
  emailInputFieldProps,
  getEmailValidationError,
} from "../utils/emailInput";
import {
  useSupplierContactExistsCheck,
  SUPPLIER_CONTACT_EXISTS_MESSAGE,
} from "../utils/useSupplierContactExistsCheck";
import MobileExistsFeedback from "../components/MobileExistsFeedback";

/** Map list/get supplier API fields to table gold / silver / balance strings */
function mapSupplierBalancesFromApi(s, fallback = {}) {
  const gold = s.gold_balance_gm ?? s.goldBalanceGm ?? s.gold ?? fallback.gold;
  const silver =
    s.silver_balance_gm ?? s.silverBalanceGm ?? s.silver ?? fallback.silver;
  const balance = s.balance ?? s.money_balance ?? fallback.balance;
  return {
    gold: gold === null || gold === undefined ? "0" : String(gold),
    silver: silver === null || silver === undefined ? "0" : String(silver),
    balance: balance === null || balance === undefined ? "0" : String(balance),
  };
}

function mapSupplierFromApi(supplier) {
  return {
    id: supplier.id,
    fullName: supplier.name || "",
    companyName: supplier.company_name || "",
    shortCode: supplier.short_code || "",
    contactNumber: supplier.contact_number || "",
    email: supplier.email || "",
    address: supplier.address || "",
    city: supplier.city || "",
    gstNumber: supplier.gst_number || supplier.GST_number || "",
    status: supplier.status || "",
    isPrimary:
      supplier.primary_supplier === 1 || supplier.primary_supplier === "1"
        ? 1
        : 0,
    ...mapSupplierBalancesFromApi(supplier),
  };
}

function SupplierTable({ masterGoldRate = 0, masterSilverRate = 0 }) {
  // const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const canKarigarConversion = hasPermission(PERMISSIONS.KARIGAR_CONVERSION_ADD) || hasPermission(PERMISSIONS.KARIGAR_CONVERSION);
  const canAddSupplier = hasPermission(PERMISSIONS.SUPPLIER_ADD);
  const canEditSupplier = hasPermission(PERMISSIONS.SUPPLIER_EDIT);
  const canSettlement = hasPermission(PERMISSIONS.SUPPLIER_SETTLEMENT_ADD) || hasPermission(PERMISSIONS.SUPPLIER_SETTLEMENT_VIEW);
  const [suppliers, setSuppliers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const suppliersCacheRef = useRef({});
  const [totalCount, setTotalCount] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Add Supplier form states
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [balance, setBalance] = useState(""); // New Balance state
  const [city, setCity] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [status, setStatus] = useState("active");
  // Add Gold and Silver states
  const [gold, setGold] = useState("");
  const [silver, setSilver] = useState("");

  const addContactExistsCheck = useSupplierContactExistsCheck(contactNumber, {
    enabled: showAddModal,
    localSuppliers: suppliers,
  });

  const editContactExistsCheck = useSupplierContactExistsCheck(
    selectedSupplier?.contactNumber ?? "",
    {
      enabled: showEditModal && Boolean(selectedSupplier),
      excludeSupplierId: selectedSupplier?.id ?? null,
      localSuppliers: suppliers,
    },
  );

  // Add state for Supplier Adjustment Modal
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedAdjustmentSupplier, setSelectedAdjustmentSupplier] =
    useState(null);
  const [adjustmentDate, setAdjustmentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentType, setAdjustmentType] = useState(""); // forbalance: 1 = Aavak, 0 = Jaavak
  const [adjustmentPaymentMode, setAdjustmentPaymentMode] = useState(null);
  const [adjustmentBankAccount, setAdjustmentBankAccount] = useState(null);
  const [adjustmentRemarks, setAdjustmentRemarks] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  // Gold/Silver adjustment states
  const [adjustmentGold, setAdjustmentGold] = useState("");
  const [adjustmentGoldType, setAdjustmentGoldType] = useState("");
  const [adjustmentGoldPaymentMode, setAdjustmentGoldPaymentMode] = useState({
    value: "gold",
    label: "Gold",
  });
  const [adjustmentGoldBankAccount, setAdjustmentGoldBankAccount] =
    useState(null);
  const [adjustmentSilver, setAdjustmentSilver] = useState("");
  const [adjustmentSilverType, setAdjustmentSilverType] = useState("");
  const [adjustmentSilverPaymentMode, setAdjustmentSilverPaymentMode] =
    useState({ value: "silver", label: "Silver" });
  const [adjustmentSilverBankAccount, setAdjustmentSilverBankAccount] =
    useState(null);

  // Metal-Amount Conversion Modal states for Supplier
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [selectedConversionSupplier, setSelectedConversionSupplier] =
    useState(null);
  const [conversionTabKey, setConversionTabKey] = useState("metalToAmount");
  const [conversionMode, setConversionMode] = useState("metalToAmount");
  const [showLiveRate, setShowLiveRate] = useState(false);
  const [goldWeight, setGoldWeight] = useState("");
  const [goldAmount, setGoldAmount] = useState("");
  const [silverWeight, setSilverWeight] = useState("");
  const [silverAmount, setSilverAmount] = useState("");
  const [goldError, setGoldError] = useState("");
  const [silverError, setSilverError] = useState("");
  const [goldValidationMsg, setGoldValidationMsg] = useState("");
  const [silverValidationMsg, setSilverValidationMsg] = useState("");
  const [goldAmountValidationMsg, setGoldAmountValidationMsg] = useState("");
  const [silverAmountValidationMsg, setSilverAmountValidationMsg] =
    useState("");

  // Utility for per gram rate (dummy, as no masterGoldRate/masterSilverRate in SupplierTable)
  const calculatePerGramRate = (rate) => rate / 10;
  const calculatePerGramSilverRate = (rate) => rate / 1000;

  // Effects for conversion modal
  useEffect(() => {
    if (showLiveRate && conversionMode === "metalToAmount") {
      if (goldWeight !== "" && !isNaN(Number(goldWeight))) {
        setGoldAmount(
          (Number(goldWeight) * calculatePerGramRate(masterGoldRate)).toFixed(2)
        );
      } else {
        setGoldAmount("");
      }
      if (silverWeight !== "" && !isNaN(Number(silverWeight))) {
        setSilverAmount(
          (
            Number(silverWeight) * calculatePerGramSilverRate(masterSilverRate)
          ).toFixed(2)
        );
      } else {
        setSilverAmount("");
      }
    }
  }, [
    goldWeight,
    silverWeight,
    showLiveRate,
    conversionMode,
    masterGoldRate,
    masterSilverRate,
  ]);
  useEffect(() => {
    if (showLiveRate && conversionMode === "amountToMetal") {
      const perGramRate = calculatePerGramRate(masterGoldRate);
      if (
        goldAmount !== "" &&
        !isNaN(Number(goldAmount)) &&
        perGramRate !== 0
      ) {
        setGoldWeight((Number(goldAmount) / perGramRate).toFixed(2));
      } else {
        setGoldWeight("");
      }
      const perGramSilverRate = calculatePerGramSilverRate(masterSilverRate);
      if (
        silverAmount !== "" &&
        !isNaN(Number(silverAmount)) &&
        perGramSilverRate !== 0
      ) {
        setSilverWeight((Number(silverAmount) / perGramSilverRate).toFixed(2));
      } else {
        setSilverWeight("");
      }
    }
  }, [
    goldAmount,
    silverAmount,
    showLiveRate,
    conversionMode,
    masterGoldRate,
    masterSilverRate,
  ]);
  useEffect(() => {
    setGoldWeight("");
    setGoldAmount("");
    setSilverWeight("");
    setSilverAmount("");
  }, [showConversionModal, conversionMode]);
  useEffect(() => {
    setGoldError("");
    setSilverError("");
  }, [showConversionModal, conversionMode, selectedConversionSupplier]);

  const invalidateSuppliersCache = useCallback(() => {
    suppliersCacheRef.current = {};
  }, []);

  const fetchSuppliers = useCallback(
    async (page = 1, limit = 10, search = "", status = "", skipCache = false) => {
      const cacheKey = `${search}-${status}`;

      if (!skipCache && suppliersCacheRef.current[cacheKey]) {
        const cached = suppliersCacheRef.current[cacheKey];
        setSuppliers(cached.items);
        setTotalCount(cached.total);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await listSuppliers({
          search: search || undefined,
          status: status !== "" ? Number(status) : undefined,
        });

        if (response?.success && Array.isArray(response.data?.suppliers)) {
          const fetchedSuppliers = response.data.suppliers
            .map(mapSupplierFromApi)
            .sort((a, b) => b.id - a.id);

          const totalItems = fetchedSuppliers.length;
          setSuppliers(fetchedSuppliers);
          setTotalCount(totalItems);

          suppliersCacheRef.current[cacheKey] = {
            items: fetchedSuppliers,
            total: totalItems,
          };
        } else {
          setSuppliers([]);
          setTotalCount(0);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching supplier details:", error);
        toast.error(error.response?.data?.message || "Failed to fetch suppliers");
        setSuppliers([]);
        setTotalCount(0);
        setIsLoading(false);
      }
    },
    [],
  );

  const updateSuppliers = async (updatedSupplier) => {
    try {
      const response = await updateSupplier({
        id: updatedSupplier.id,
        name: updatedSupplier.fullName,
        company_name: updatedSupplier.companyName,
        short_code: updatedSupplier.shortCode,
        contact_number: updatedSupplier.contactNumber,
        email: updatedSupplier.email,
        address: updatedSupplier.residentialAddress,
        city: updatedSupplier.city,
        gst_number: updatedSupplier.gstNumber,
        status: updatedSupplier.status,
        primary_supplier: updatedSupplier.isPrimary,
        gold_balance_gm: parseFloat(updatedSupplier.gold) || 0,
        silver_balance_gm: parseFloat(updatedSupplier.silver) || 0,
        balance: parseFloat(updatedSupplier.balance) || 0,
      });

      if (response?.success) {
        toast.success("Supplier updated successfully");

        let apiSupplier = response?.data?.supplier;
        if (!apiSupplier?.id) {
          try {
            const getRes = await getSupplier({ id: updatedSupplier.id });
            apiSupplier = getRes?.data?.supplier || getRes?.data || null;
          } catch (getErr) {
            console.warn("Could not refresh supplier after update:", getErr);
          }
        }

        setSuppliers((prev) =>
          prev.map((supplier) => {
            if (supplier.id === updatedSupplier.id) {
              if (apiSupplier?.id) {
                return mapSupplierFromApi(apiSupplier);
              }
              return {
                ...supplier,
                fullName: updatedSupplier.fullName,
                companyName: updatedSupplier.companyName,
                shortCode: updatedSupplier.shortCode,
                contactNumber: updatedSupplier.contactNumber,
                email: updatedSupplier.email,
                address: updatedSupplier.residentialAddress || supplier.address,
                city: updatedSupplier.city,
                gstNumber: updatedSupplier.gstNumber,
                status: updatedSupplier.status,
                isPrimary: updatedSupplier.isPrimary,
                gold: String(updatedSupplier.gold ?? "0"),
                silver: String(updatedSupplier.silver ?? "0"),
                balance: String(updatedSupplier.balance ?? "0"),
              };
            }
            if (updatedSupplier.isPrimary === 1 && supplier.isPrimary === 1) {
              return { ...supplier, isPrimary: 0 };
            }
            return supplier;
          }),
        );

        invalidateSuppliersCache();
        fetchSuppliers(
          currentPage,
          itemsPerPage,
          searchQuery,
          statusFilter,
          true,
        );
        return true;
      }

      toast.error(response?.message || "Failed to update supplier");
      return false;
    } catch (error) {
      console.error("Error updating supplier details:", error);
      toast.error(error.response?.data?.message || "Failed to update supplier");
      return false;
    }
  };

  const generateShortCode = (name) => {
    return name
      .split(" ")
      .filter((word) => word && !word.includes("-"))
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  useEffect(() => {
    // Debounce search query
    const timeoutId = setTimeout(() => {
      fetchSuppliers(currentPage, itemsPerPage, searchQuery, statusFilter);
    }, searchQuery ? 200 : 0); // 200ms debounce for search, immediate for other changes

    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, fetchSuppliers]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in SupplierTable, refetching suppliers...', event.detail);
      invalidateSuppliersCache();
      setCurrentPage(1);
      fetchSuppliers(1, itemsPerPage, searchQuery, statusFilter, true);
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, statusFilter, fetchSuppliers, invalidateSuppliersCache]);

  // Real-time row updates (same event Karigar page uses)
  useEffect(() => {
    const socket = createErpSocket();

    socket.on("supplier_updated", (data) => {
      if (!data?.supplier) return;

      const updatedIsPrimary =
        data.supplier.primary_supplier === 1 ||
        data.supplier.primary_supplier === "1";

      setSuppliers((prev) =>
        prev.map((supplier) => {
          if (supplier.id === data.supplier.id) {
            return mapSupplierFromApi(data.supplier);
          }
          if (updatedIsPrimary && supplier.isPrimary === 1) {
            return { ...supplier, isPrimary: 0 };
          }
          return supplier;
        }),
      );
      invalidateSuppliersCache();
    });

    return () => {
      socket.off("supplier_updated");
      socket.disconnect();
    };
  }, [invalidateSuppliersCache]);

  useEffect(() => {
    setShortCode(generateShortCode(companyName));
  }, [companyName]);

  // Fetch bank accounts for dropdown in adjustment modal
  const fetchBankDetails = async () => {
    try {
      const response = await listBankAccounts();
      const rows = Array.isArray(response?.data?.bank_accounts)
        ? response.data.bank_accounts
        : [];

      if (response?.success && Array.isArray(rows)) {
        const fetchedBankDetails = rows.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance: bank.opening_balance ? `₹${bank.opening_balance}` : "₹0",
          status:
            bank.status === "1" || bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === "1" || bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.use_for_invoice === 1,
          useForPOS: bank.use_for_pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);

        const bankOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
          data: bank,
        }));

        setBankAccounts(bankOptions);
      } else if (response?.success && rows.length === 0) {
        setBankAccounts([]);
        toast("No bank accounts found");
      } else {
        console.error("Unexpected data format:", response);
        setBankAccounts([]);
        toast.error(response?.message || "Failed to fetch bank details");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankAccounts([]);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch bank details"
      );
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const AMOUNT_PAYMENT_MODE_OPTIONS = [
    { value: "cash", label: "Cash" },
    { value: "upi", label: "UPI" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "card", label: "Card" },
  ];

  const handleEditClick = (supplier) => {
    if (!canEditSupplier) {
      toast.error("You do not have permission to edit supplier/karigar.");
      return;
    }
    setSelectedSupplier({
      ...supplier,
      status: supplier.status,
      residentialAddress: supplier.address || "",
      balance: supplier.balance || "", // New Balance field
      gold: supplier.gold || "",
      silver: supplier.silver || "",
    });
    setShowEditModal(true);
  };

  const handleSaveChanges = async () => {
    if (selectedSupplier.status === "0" && selectedSupplier.isPrimary === 1) {
      toast.error("Cannot set an inactive supplier as primary.");
      return;
    }

    const mobileError = getMobileValidationError(
      selectedSupplier?.contactNumber,
      { required: true },
    );
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(selectedSupplier?.email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (editContactExistsCheck.exists) {
      toast.error(SUPPLIER_CONTACT_EXISTS_MESSAGE);
      return;
    }

    const success = await updateSuppliers(selectedSupplier);
    if (success) {
      setShowEditModal(false);
    }
  };

  // No client-side filtering needed - all filtering is done on the backend
  // suppliers already contains the paginated and filtered data from the API
  const displayedSuppliers = suppliers;

  // Handle Page Change
  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Data will be fetched automatically by useEffect
    }
  };

  // Handle Items Per Page Change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    invalidateSuppliersCache();
    // Data will be fetched automatically by useEffect
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    const mobileError = getMobileValidationError(contactNumber, {
      required: true,
    });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (addContactExistsCheck.exists) {
      toast.error(SUPPLIER_CONTACT_EXISTS_MESSAGE);
      return;
    }

    try {
      const response = await createSupplier({
        name: fullName,
        company_name: companyName,
        short_code: shortCode,
        contact_number: contactNumber,
        email: email,
        address: address,
        city: city,
        gst_number: gstNumber,
        primary_supplier: 0,
        gold_balance_gm: parseFloat(gold) || 0,
        silver_balance_gm: parseFloat(silver) || 0,
        balance: parseFloat(balance) || 0,
      });

      // Check if the response indicates success
      if (response?.success) {
        toast.success("Supplier added successfully");

        // Reset form fields
        setFullName("");
        setCompanyName("");
        setShortCode("");
        setContactNumber("");
        setEmail("");
        setAddress("");
        setBalance(""); // Reset Balance
        setCity("");
        setGstNumber("");
        setStatus("active");
        setGold(""); // Reset Gold
        setSilver(""); // Reset Silver
        setShowAddModal(false);

        invalidateSuppliersCache();
        fetchSuppliers(currentPage, itemsPerPage, searchQuery, statusFilter, true);
      } else {
        // Handle server error response
        toast.error(response?.message || "Failed to add supplier");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Failed to add supplier");
    }
  };

  const handleAdjustmentClick = (supplier) => {
    if (!canSettlement) {
      toast.error("You do not have permission for supplier settlement.");
      return;
    }
    setSelectedAdjustmentSupplier(supplier);
    setAdjustmentDate(new Date().toISOString().split("T")[0]);
    setAdjustmentAmount("");
    setAdjustmentType("");
    setAdjustmentPaymentMode(null);
    setAdjustmentBankAccount(null);
    setAdjustmentRemarks("");
    // Reset gold/silver fields
    setAdjustmentGold("");
    setAdjustmentGoldType("");
    setAdjustmentGoldPaymentMode({ value: "gold", label: "Gold" });
    setAdjustmentGoldBankAccount(null);
    setAdjustmentSilver("");
    setAdjustmentSilverType("");
    setAdjustmentSilverPaymentMode({ value: "silver", label: "Silver" });
    setAdjustmentSilverBankAccount(null);
    setShowAdjustmentModal(true);
  };

  const handleSupplierAdjustment = async (e) => {
    e.preventDefault();
    if (!selectedAdjustmentSupplier) return;
    try {
      const payload = {
        type: "SUP_AD",
        id: selectedAdjustmentSupplier.id,
        by_user: 0, // Set to 0 for now
        date: adjustmentDate,
        remarks: adjustmentRemarks,
        // Amount
        balance: parseFloat(adjustmentAmount) || 0,
        forbalance: adjustmentType === "credit" ? 1 : 0,
        balance_mode: adjustmentPaymentMode?.value || "",
        balance_bank_type: adjustmentBankAccount?.value || 0,
        // Gold
        gold: parseFloat(adjustmentGold) || 0,
        forgold: adjustmentGoldType === "credit" ? 1 : 0,
        gold_mode: adjustmentGoldPaymentMode?.value || "",
        gold_bank_type: adjustmentGoldBankAccount?.value || 0,
        // Silver
        silver: parseFloat(adjustmentSilver) || 0,
        forsilver: adjustmentSilverType === "credit" ? 1 : 0,
        silver_mode: adjustmentSilverPaymentMode?.value || "",
        silver_bank_type: adjustmentSilverBankAccount?.value || 0,
      };
      await postSupplierConversion(payload);
      toast.success("Adjustment successful");
      setShowAdjustmentModal(false);
      invalidateSuppliersCache();
      fetchSuppliers(currentPage, itemsPerPage, searchQuery, statusFilter, true);
    } catch (error) {
      console.error("Error in supplier adjustment:", error);
      toast.error(error.response?.data?.error || "Adjustment failed");
    }
  };

  const handleConversionClick = (supplier) => {
    if (!canKarigarConversion) {
      toast.error("You do not have permission for karigar conversion.");
      return;
    }
    setSelectedConversionSupplier(supplier);
    setConversionTabKey("metalToAmount");
    setShowConversionModal(true);
  };

  const handleConversionSubmit = async () => {
    const payload = {
      type: "METALCON",
      id: selectedConversionSupplier.id,
      conversion_type: conversionMode === "metalToAmount" ? "MTOA" : "ATOM",
      gold: parseFloat(goldWeight) || 0,
      gold_amt: parseFloat(goldAmount) || 0,
      silver: parseFloat(silverWeight) || 0,
      silver_amt: parseFloat(silverAmount) || 0,
    };
    try {
      await postSupplierConversion(payload);
      toast.success("Conversion successful");
      setShowConversionModal(false);
      invalidateSuppliersCache();
      fetchSuppliers(currentPage, itemsPerPage, searchQuery, statusFilter, true);
    } catch (error) {
      console.error("Error in conversion:", error);
      toast.error(error.response?.data?.error || "Conversion failed");
    }
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className={`card w-100 position-relative overflow-hidden ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Supplier Table</h4>
            {canAddSupplier && (
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => {
                  if (!canAddSupplier) {
                    toast.error("You do not have permission to add supplier/karigar.");
                    return;
                  }
                  setShowAddModal(true);
                }}
              >
                <Icon
                  icon="solar:add-circle-bold"
                  width="18"
                  height="18"
                  className="me-2"
                />
                Add Supplier
              </button>
            )}
          </div>

          <div className="card-body p-4">
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                  invalidateSuppliersCache();
                }}
              />
              <select
                className="form-select w-auto ms-3"
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                  invalidateSuppliersCache();
                }}
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
                <option value="primary">Primary</option>
              </select>
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>City</th>
                    <th>GST</th>
                    <th>Metal</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedSuppliers.length > 0 ? (
                    displayedSuppliers.map((supplier) => (
                      <tr key={supplier.id}>
                        <td>
                          {supplier.companyName}
                          {supplier.isPrimary === 1 && (
                            <span className="badge bg-warning text-dark ms-2">
                              Primary
                            </span>
                          )}
                          <br />
                          <span className="text-muted">
                            ({supplier.fullName})
                          </span>
                        </td>
                        <td>{supplier.contactNumber}</td>
                        <td>{supplier.city}</td>
                        <td>{supplier.gstNumber}</td>
                        {/* Metal column: Gold and Silver badges */}
                        <td>
                          <div>
                            <span
                              className={`badge ${
                                parseFloat(supplier.gold) > 0
                                  ? "bg-success-subtle text-success"
                                  : parseFloat(supplier.gold) < 0
                                  ? "bg-danger-subtle text-danger"
                                  : "bg-light text-dark"
                              }`}
                              style={{ fontSize: "0.85rem" }}
                            >
                              Gold: {parseFloat(supplier.gold || 0).toFixed(2)}{" "}
                              gm
                            </span>
                          </div>
                          <div className="mt-1">
                            <span
                              className={`badge ${
                                parseFloat(supplier.silver) > 0
                                  ? "bg-success-subtle text-success"
                                  : parseFloat(supplier.silver) < 0
                                  ? "bg-danger-subtle text-danger"
                                  : "bg-light text-dark"
                              }`}
                              style={{ fontSize: "0.85rem" }}
                            >
                              Silver:{" "}
                              {parseFloat(supplier.silver || 0).toFixed(2)} gm
                            </span>
                          </div>
                        </td>
                        {/* Balance column: Balance badge */}
                        <td>
                          <span
                            className={`badge ${
                              parseFloat(supplier.balance) > 0
                                ? "bg-success-subtle text-success"
                                : parseFloat(supplier.balance) < 0
                                ? "bg-danger-subtle text-danger"
                                : "bg-light text-dark"
                            }`}
                            style={{ fontSize: "0.85rem" }}
                          >
                            ₹{parseFloat(supplier.balance || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              supplier.status === "1" || supplier.status === 1
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {supplier.status === "1" || supplier.status === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>
                        <td>
                          {canEditSupplier && (
                            <button
                              className={`btn btn-primary btn-sm ${buttonClass}`}
                              onClick={() => handleEditClick(supplier)}
                            >
                              <Icon
                                icon="solar:pen-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                          )}
                          {/* Supplier Adjustment Button */}
                          {canSettlement && (
                            <button
                              className={`btn btn-secondary btn-sm ms-2 ${buttonClass}`}
                              onClick={() => handleAdjustmentClick(supplier)}
                              type="button"
                            >
                              <Icon
                                icon="fa6-solid:hand-holding-dollar"
                                width="16"
                                height="16"
                              />
                            </button>
                          )}
                          {/* Conversion Button */}
                          {canKarigarConversion && (
                            <button
                              className={`btn btn-info btn-sm ms-2 ${buttonClass}`}
                              type="button"
                              onClick={() => handleConversionClick(supplier)}
                            >
                              <Icon
                                icon="material-symbols:sync-alt"
                                width="16"
                                height="16"
                              />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No suppliers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* Add Supplier Modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        // size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  Full Name 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  Company Name 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  Short Code 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Short Code"
                  value={shortCode}
                  onChange={(e) => setShortCode(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  Contact Number 
                </Form.Label>
                <Form.Control
                  {...mobileInputFieldProps}
                  required
                  placeholder="Enter Contact Number"
                  value={contactNumber}
                  onChange={(e) =>
                    setContactNumber(sanitizeMobileInput(e.target.value))
                  }
                  isInvalid={addContactExistsCheck.exists}
                />
                <MobileExistsFeedback
                  exists={addContactExistsCheck.exists}
                  checking={addContactExistsCheck.checking}
                  message={SUPPLIER_CONTACT_EXISTS_MESSAGE}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  {...emailInputFieldProps}
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  Address 
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  required
                  placeholder="Enter Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Col>
            </Row>
            {/* Balance, Gold, Silver Fields */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  City 
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>GST Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter GST Number"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Gold</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter Gold in gm"
                  value={gold}
                  onChange={(e) => setGold(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Silver</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter Silver in gm"
                  value={silver}
                  onChange={(e) => setSilver(e.target.value)}
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Balance</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Balance"
                  value={balance}
                  onChange={(e) =>
                    setBalance(e.target.value.replace(/[^0-9.-]/g, ""))
                  }
                />
              </Col>
            </Row>

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <Button
                type="submit"
                variant="primary"
                className={buttonClass}
                disabled={
                  addContactExistsCheck.exists || addContactExistsCheck.checking
                }
              >
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        // size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSupplier && (
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedSupplier.fullName}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        fullName: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedSupplier.companyName}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        companyName: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Short Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedSupplier.shortCode}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        shortCode: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Contact Number</Form.Label>
                  <Form.Control
                    {...mobileInputFieldProps}
                    value={selectedSupplier.contactNumber}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        contactNumber: sanitizeMobileInput(e.target.value),
                      })
                    }
                    isInvalid={editContactExistsCheck.exists}
                  />
                  <MobileExistsFeedback
                    exists={editContactExistsCheck.exists}
                    checking={editContactExistsCheck.checking}
                    message={SUPPLIER_CONTACT_EXISTS_MESSAGE}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    {...emailInputFieldProps}
                    value={selectedSupplier.email}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        email: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter Address"
                    value={selectedSupplier.residentialAddress}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        residentialAddress: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
              {/* Balance, Gold, Silver Fields */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter City"
                    value={selectedSupplier.city}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        city: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>GST Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter GST Number"
                    value={selectedSupplier.gstNumber}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        gstNumber: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Gold</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={selectedSupplier.gold || ""}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        gold: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Silver</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={selectedSupplier.silver || ""}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        silver: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Balance</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Balance"
                    value={selectedSupplier.balance}
                    onChange={(e) =>
                      setSelectedSupplier({
                        ...selectedSupplier,
                        balance: e.target.value.replace(/[^0-9.-]/g, ""),
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Supplier Status</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      label="Active"
                      name="status"
                      value="1"
                      checked={
                        selectedSupplier.status === "1" ||
                        selectedSupplier.status === 1
                      }
                      onChange={(e) =>
                        setSelectedSupplier({
                          ...selectedSupplier,
                          status: e.target.value,
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="Inactive"
                      name="status"
                      value="0"
                      checked={selectedSupplier.status === "0"}
                      onChange={(e) =>
                        setSelectedSupplier({
                          ...selectedSupplier,
                          status: e.target.value,
                        })
                      }
                    />
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Primary Supplier</Form.Label>
                  <Form.Check
                    type="checkbox"
                    label="Set as Primary Supplier"
                    checked={selectedSupplier.isPrimary === 1}
                    onChange={(e) => {
                      setSelectedSupplier({
                        ...selectedSupplier,
                        isPrimary: e.target.checked ? 1 : 0,
                      });
                    }}
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
            disabled={
              editContactExistsCheck.exists || editContactExistsCheck.checking
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Supplier Adjustment Modal */}
      <Modal
        show={showAdjustmentModal}
        onHide={() => setShowAdjustmentModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Supplier Adjustment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSupplierAdjustment}>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={adjustmentDate}
                  onChange={(e) => setAdjustmentDate(e.target.value)}
                />
              </Col>
            </Row>
            {/* Gold Transaction Row */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Gold Transaction</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter gold in gm"
                  value={adjustmentGold}
                  onChange={(e) => setAdjustmentGold(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>For Gold</Form.Label>
                <div className="d-flex gap-3 mb-2">
                  <Form.Check
                    type="radio"
                    label="Aavak"
                    name="adjustmentGoldType"
                    value="credit"
                    checked={adjustmentGoldType === "credit"}
                    onChange={() => setAdjustmentGoldType("credit")}
                  />
                  <Form.Check
                    type="radio"
                    label="Jaavak"
                    name="adjustmentGoldType"
                    value="debit"
                    checked={adjustmentGoldType === "debit"}
                    onChange={() => setAdjustmentGoldType("debit")}
                  />
                </div>
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">Payment Mode</Form.Label>
                <Select
                  options={[{ value: "gold", label: "Gold" }]}
                  value={adjustmentGoldPaymentMode}
                  onChange={setAdjustmentGoldPaymentMode}
                  isClearable={false}
                  isSearchable={false}
                  placeholder="Select Payment Mode"
                />
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">
                  Received In / Paid From
                </Form.Label>
                <Select
                  options={bankAccounts}
                  value={adjustmentGoldBankAccount}
                  onChange={setAdjustmentGoldBankAccount}
                  isLoading={loadingBankAccounts}
                  placeholder="Select Bank"
                  isClearable
                />
              </Col>
            </Row>
            {/* Silver Transaction Row */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Silver Transaction</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter silver in gm"
                  value={adjustmentSilver}
                  onChange={(e) => setAdjustmentSilver(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>For Silver</Form.Label>
                <div className="d-flex gap-3 mb-2">
                  <Form.Check
                    type="radio"
                    label="Aavak"
                    name="adjustmentSilverType"
                    value="credit"
                    checked={adjustmentSilverType === "credit"}
                    onChange={() => setAdjustmentSilverType("credit")}
                  />
                  <Form.Check
                    type="radio"
                    label="Jaavak"
                    name="adjustmentSilverType"
                    value="debit"
                    checked={adjustmentSilverType === "debit"}
                    onChange={() => setAdjustmentSilverType("debit")}
                  />
                </div>
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">Payment Mode</Form.Label>
                <Select
                  options={[{ value: "silver", label: "Silver" }]}
                  value={adjustmentSilverPaymentMode}
                  onChange={setAdjustmentSilverPaymentMode}
                  isClearable={false}
                  isSearchable={false}
                  placeholder="Select Payment Mode"
                />
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">
                  Received In / Paid From
                </Form.Label>
                <Select
                  options={bankAccounts}
                  value={adjustmentSilverBankAccount}
                  onChange={setAdjustmentSilverBankAccount}
                  isLoading={loadingBankAccounts}
                  placeholder="Select Bank"
                  isClearable
                />
              </Col>
            </Row>
            {/* Amount Transaction Row (existing) */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Amount Transaction</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  placeholder="Enter amount"
                  value={adjustmentAmount}
                  onChange={(e) => setAdjustmentAmount(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>For Amount</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Aavak"
                    name="adjustmentType"
                    value="credit"
                    checked={adjustmentType === "credit"}
                    onChange={() => setAdjustmentType("credit")}
                  />
                  <Form.Check
                    type="radio"
                    label="Jaavak"
                    name="adjustmentType"
                    value="debit"
                    checked={adjustmentType === "debit"}
                    onChange={() => setAdjustmentType("debit")}
                  />
                </div>
              </Col>
              <Col md={3}>
                <Form.Label>Payment Mode</Form.Label>
                <Select
                  options={AMOUNT_PAYMENT_MODE_OPTIONS}
                  value={adjustmentPaymentMode}
                  onChange={setAdjustmentPaymentMode}
                  isClearable
                  isSearchable
                  placeholder="Select Mode"
                />
              </Col>
              <Col md={3}>
                <Form.Label>Received In / Paid From</Form.Label>
                <Select
                  options={bankAccounts}
                  value={adjustmentBankAccount}
                  onChange={setAdjustmentBankAccount}
                  isLoading={loadingBankAccounts}
                  placeholder="Select Bank"
                  isClearable
                />
              </Col>
            </Row>
            <Row className="mb-3"></Row>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Enter remarks"
                  value={adjustmentRemarks}
                  onChange={(e) => setAdjustmentRemarks(e.target.value)}
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-end">
              <Button type="submit" variant="primary" className={buttonClass}>
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Metal-Amount Conversion Modal */}
      <Modal
        show={showConversionModal}
        onHide={() => setShowConversionModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <div className="d-flex w-100 flex-row align-items-center justify-content-between gap-2">
            <Modal.Title className="mb-0">Metal-Amount Conversion</Modal.Title>
            {selectedConversionSupplier && (
              <div className="d-flex flex-row flex-wrap gap-2 align-items-center justify-content-end w-auto">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier.gold) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier.gold) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{ fontSize: "0.85rem" }}
                >
                  Gold:{" "}
                  {parseFloat(selectedConversionSupplier?.gold || 0).toFixed(2)}{" "}
                  gm
                </span>
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier.silver) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier.silver) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{ fontSize: "0.85rem" }}
                >
                  Silver:{" "}
                  {parseFloat(selectedConversionSupplier?.silver || 0).toFixed(
                    2
                  )}{" "}
                  gm
                </span>
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier.balance) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier.balance) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{ fontSize: "0.85rem" }}
                >
                  Balance: ₹
                  {parseFloat(selectedConversionSupplier?.balance || 0).toFixed(
                    2
                  )}
                </span>
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              {/* Swap icon above both rows on md+, between rows on small screens */}
              <div className="d-none d-md-flex justify-content-center mb-2">
                <Button
                  variant="light"
                  size="sm"
                  className="rounded-circle border"
                  style={{ padding: 0, width: 32, height: 32 }}
                  onClick={() =>
                    setConversionMode(
                      conversionMode === "metalToAmount"
                        ? "amountToMetal"
                        : "metalToAmount"
                    )
                  }
                  aria-label="Swap conversion direction"
                >
                  <Icon
                    icon="material-symbols:swap-horiz"
                    width="20"
                    height="20"
                  />
                </Button>
              </div>
              {/* Gold/Amount Row */}
              <div className="d-flex flex-column flex-md-row align-items-center mb-3">
                <div className="w-100 order-1 order-md-1">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Gold (gm)"
                      : "Amount (₹)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Enter gold in gm"
                        : "Enter gold amount"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionSupplier?.gold,
                            goldWeight
                          )
                        : enforceMinusIfNegative(
                            selectedConversionSupplier?.balance,
                            goldAmount
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        const val = enforceMinusIfNegative(
                          selectedConversionSupplier?.gold,
                          e.target.value
                        );
                        const validationMsg = getMaxAllowedValidationMessage(
                          selectedConversionSupplier?.gold,
                          val,
                          "Gold"
                        );
                        setGoldValidationMsg(validationMsg);
                        if (!validationMsg) {
                          setGoldWeight(val);
                        }
                      } else {
                        const val = enforceMinusIfNegative(
                          selectedConversionSupplier?.balance,
                          e.target.value
                        );
                        const validationMsg =
                          getMaxAllowedAmountValidationMessage(
                            selectedConversionSupplier?.balance,
                            val,
                            silverAmount
                          );
                        setGoldAmountValidationMsg(validationMsg);
                        setSilverAmountValidationMsg(validationMsg);
                        if (!validationMsg) {
                          setGoldAmount(val);
                        }
                      }
                    }}
                    disabled={
                      showLiveRate && conversionMode === "amountToMetal"
                        ? false
                        : showLiveRate && conversionMode === "metalToAmount"
                        ? false
                        : false
                    }
                  />
                  {conversionMode === "metalToAmount" && goldValidationMsg && (
                    <div className="text-danger" style={{ fontSize: "0.9em" }}>
                      {goldValidationMsg}
                    </div>
                  )}
                  {conversionMode === "amountToMetal" &&
                    goldAmountValidationMsg && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "0.9em" }}
                      >
                        {goldAmountValidationMsg}
                      </div>
                    )}
                </div>
                <div
                  className="w-100 d-flex justify-content-center order-2 order-md-2 my-2 my-md-0"
                  style={{ minWidth: 0 }}
                >
                  <span className="fw-bold">converts to</span>
                </div>
                <div className="w-100 order-3 order-md-3">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Amount (₹)"
                      : "Gold (gm)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Gold Amount"
                        : "Gold in gm"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionSupplier?.gold,
                            goldAmount
                          )
                        : enforceMinusIfNegative(
                            selectedConversionSupplier?.balance,
                            goldWeight
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        setGoldAmount(
                          enforceMinusIfNegative(
                            selectedConversionSupplier?.gold,
                            e.target.value
                          )
                        );
                      } else {
                        setGoldWeight(
                          enforceMinusIfNegative(
                            selectedConversionSupplier?.balance,
                            e.target.value
                          )
                        );
                      }
                    }}
                    disabled={showLiveRate ? true : false}
                  />
                </div>
              </div>
              {/* Swap icon between rows on small screens only (vertical arrows) */}
              <div className="d-flex d-md-none justify-content-center mb-2">
                <Button
                  variant="light"
                  size="sm"
                  className="rounded-circle border"
                  style={{ padding: 0, width: 32, height: 32 }}
                  onClick={() =>
                    setConversionMode(
                      conversionMode === "metalToAmount"
                        ? "amountToMetal"
                        : "metalToAmount"
                    )
                  }
                  aria-label="Swap conversion direction"
                >
                  <Icon
                    icon="material-symbols:swap-vert"
                    width="20"
                    height="20"
                  />
                </Button>
              </div>
              {/* Silver/Amount Row (now with live rate logic) */}
              <div className="d-flex flex-column flex-md-row align-items-center mb-3">
                <div className="w-100 order-1 order-md-1">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Silver (gm)"
                      : "Amount (₹)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Enter silver in gm"
                        : "Enter silver amount"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionSupplier?.silver,
                            silverWeight
                          )
                        : enforceMinusIfNegative(
                            selectedConversionSupplier?.balance,
                            silverAmount
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        const val = enforceMinusIfNegative(
                          selectedConversionSupplier?.silver,
                          e.target.value
                        );
                        const validationMsg = getMaxAllowedValidationMessage(
                          selectedConversionSupplier?.silver,
                          val,
                          "Silver"
                        );
                        setSilverValidationMsg(validationMsg);
                        if (!validationMsg) {
                          setSilverWeight(val);
                        }
                      } else {
                        const val = enforceMinusIfNegative(
                          selectedConversionSupplier?.balance,
                          e.target.value
                        );
                        const validationMsg =
                          getMaxAllowedAmountValidationMessage(
                            selectedConversionSupplier?.balance,
                            goldAmount,
                            val
                          );
                        setGoldAmountValidationMsg(validationMsg);
                        setSilverAmountValidationMsg(validationMsg);
                        if (!validationMsg) {
                          setSilverAmount(val);
                        }
                      }
                    }}
                    disabled={
                      showLiveRate && conversionMode === "amountToMetal"
                        ? false
                        : showLiveRate && conversionMode === "metalToAmount"
                        ? false
                        : false
                    }
                  />
                  {conversionMode === "metalToAmount" &&
                    silverValidationMsg && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "0.9em" }}
                      >
                        {silverValidationMsg}
                      </div>
                    )}
                  {conversionMode === "amountToMetal" &&
                    silverAmountValidationMsg && (
                      <div
                        className="text-danger"
                        style={{ fontSize: "0.9em" }}
                      >
                        {silverAmountValidationMsg}
                      </div>
                    )}
                </div>
                <div
                  className="w-100 d-flex justify-content-center order-2 order-md-2 my-2 my-md-0"
                  style={{ minWidth: 0 }}
                >
                  <span className="fw-bold">converts to</span>
                </div>
                <div className="w-100 order-3 order-md-3">
                  <Form.Label className="mb-1">
                    {conversionMode === "metalToAmount"
                      ? "Amount (₹)"
                      : "Silver (gm)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      conversionMode === "metalToAmount"
                        ? "Silver Amount"
                        : "Silver in gm"
                    }
                    value={
                      conversionMode === "metalToAmount"
                        ? enforceMinusIfNegative(
                            selectedConversionSupplier?.silver,
                            silverAmount
                          )
                        : enforceMinusIfNegative(
                            selectedConversionSupplier?.balance,
                            silverWeight
                          )
                    }
                    onChange={(e) => {
                      if (conversionMode === "metalToAmount") {
                        setSilverAmount(
                          enforceMinusIfNegative(
                            selectedConversionSupplier?.silver,
                            e.target.value
                          )
                        );
                      } else {
                        setSilverWeight(
                          enforceMinusIfNegative(
                            selectedConversionSupplier?.balance,
                            e.target.value
                          )
                        );
                      }
                    }}
                    disabled={showLiveRate ? true : false}
                  />
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {/* Prediction/Badge Row for Metal to Amount */}
          {conversionMode === "metalToAmount" && (
            <div className="w-100 mb-3">
              {/* Gold Row */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier?.gold) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier?.gold) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {parseFloat(selectedConversionSupplier?.gold || 0).toFixed(2)}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    const result = prev - upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    return (prev - upd).toFixed(2);
                  })()}
                </span>
              </div>
              {/* Silver Row */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier?.silver) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier?.silver) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {parseFloat(selectedConversionSupplier?.silver || 0).toFixed(
                    2
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    const result = prev - upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    return (prev - upd).toFixed(2);
                  })()}
                </span>
              </div>
              {/* Balance Row (with calculation for both goldAmount and silverAmount) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier?.balance) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier?.balance) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {parseFloat(selectedConversionSupplier?.balance || 0).toFixed(
                    2
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    const result = prev + updGold + updSilver;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    return (prev + updGold + updSilver).toFixed(2);
                  })()}
                </span>
              </div>
            </div>
          )}
          {/* End prediction row */}
          {/* Prediction/Badge Row for Amount to Metal */}
          {conversionMode === "amountToMetal" && (
            <div className="w-100 mb-3">
              {/* Gold Row (with calculation for amount to metal) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier?.gold) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier?.gold) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {parseFloat(selectedConversionSupplier?.gold || 0).toFixed(2)}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    const result = prev + upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Gold:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.gold) || 0;
                    const upd = parseFloat(goldWeight) || 0;
                    return (prev + upd).toFixed(2);
                  })()}
                </span>
              </div>
              {/* Silver Row (with calculation for amount to metal) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier?.silver) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier?.silver) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {parseFloat(selectedConversionSupplier?.silver || 0).toFixed(
                    2
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    const result = prev + upd;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 90,
                    justifyContent: "center",
                  }}
                >
                  Silver:{" "}
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.silver) || 0;
                    const upd = parseFloat(silverWeight) || 0;
                    return (prev + upd).toFixed(2);
                  })()}
                </span>
              </div>
              {/* Balance Row (with calculation for amount to metal, both goldAmount and silverAmount) */}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span
                  className={`badge ${
                    parseFloat(selectedConversionSupplier?.balance) > 0
                      ? "bg-success-subtle text-success"
                      : parseFloat(selectedConversionSupplier?.balance) < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark"
                  }`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {parseFloat(selectedConversionSupplier?.balance || 0).toFixed(
                    2
                  )}
                </span>
                <Icon
                  icon="material-symbols:arrow-forward"
                  width="20"
                  height="20"
                />
                <span
                  className={`badge ${(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    const result = prev - updGold - updSilver;
                    return result > 0
                      ? "bg-success-subtle text-success"
                      : result < 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-light text-dark";
                  })()}`}
                  style={{
                    fontSize: "0.95rem",
                    minWidth: 110,
                    justifyContent: "center",
                  }}
                >
                  Balance: ₹
                  {(() => {
                    const prev =
                      parseFloat(selectedConversionSupplier?.balance) || 0;
                    const updGold = parseFloat(goldAmount) || 0;
                    const updSilver = parseFloat(silverAmount) || 0;
                    return (prev - updGold - updSilver).toFixed(2);
                  })()}
                </span>
              </div>
            </div>
          )}
          {/* End prediction row */}
          <div className="d-flex w-100 align-items-center justify-content-between">
            <Form.Check
              type="checkbox"
              id="liveRateCheckbox"
              label="Live Rate"
              className="mb-0"
              checked={showLiveRate}
              onChange={(e) => setShowLiveRate(e.target.checked)}
            />
            <Button variant="primary" className={buttonClass} onClick={handleConversionSubmit}>
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SupplierTable;
